import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useBranch } from '../state/BranchContext';
import type { DriverView, StaffBoard, StaffOrder } from '../api/types';
import { Alert, Spinner, money, statusLabel, statusTone } from '../components/common';

const REFRESH_MS = 20_000;

/** Buttons read as actions, not as state names. */
const ACTION_LABEL: Record<string, string> = {
  Preparing: 'Start making',
  ReadyForCollection: 'Ready for collection',
  OutForDelivery: 'Out for delivery',
  Completed: 'Done',
  Cancelled: 'Cancel',
};

/**
 * The kitchen board. Polls rather than pushes: the counter tablet only needs to
 * be roughly live, and polling survives the flaky wifi in a food court far
 * better than a socket that silently dies.
 */
export function StaffBoardPage() {
  const [board, setBoard] = useState<StaffBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Head office can narrow the board to one shop. Somebody who works at a shop
  // has this decided for them by the server, so the control never appears.
  const { branches, onlyOne: onlyOneBranch } = useBranch();
  const [branchFilter, setBranchFilter] = useState<string | null>(null);

  // Held in refs so the polling loop never closes over a stale value.
  const showDoneRef = useRef(showDone);
  showDoneRef.current = showDone;
  const branchFilterRef = useRef(branchFilter);
  branchFilterRef.current = branchFilter;

  const load = useCallback(async () => {
    try {
      setBoard(await api.staffBoard(showDoneRef.current, branchFilterRef.current));
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();

    const timer = window.setInterval(() => void load(), REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    void load();
  }, [showDone, branchFilter, load]);

  // Approved drivers who are on shift. Reloaded with the board so someone
  // clocking on appears without the screen being refreshed by hand.
  const [drivers, setDrivers] = useState<DriverView[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchDrivers = () =>
      api
        .driversAvailable()
        .then((list) => !cancelled && setDrivers(list))
        .catch(() => undefined);

    void fetchDrivers();
    const timer = window.setInterval(fetchDrivers, REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  async function assign(order: StaffOrder, driverProfileId: string) {
    setBusyId(order.id);
    setError(null);

    try {
      if (driverProfileId) {
        await api.assignDriver(order.id, driverProfileId);
      } else {
        await api.unassignDriver(order.id);
      }

      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function advance(order: StaffOrder, status: string) {
    if (status === 'Cancelled' && !window.confirm(`Cancel order ${order.orderNumber}?`)) {
      return;
    }

    setBusyId(order.id);

    try {
      await api.staffAdvance(order.id, status);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (!board) return <Spinner label="Loading the board…" />;

  return (
    <section className="page">
      <header className="page__head">
        <div>
          <h1>Order board{board?.branchName ? ` · ${board.branchName}` : ''}</h1>
          <p className="muted small">
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString('en-ZA')}` : 'Updating…'} · refreshes
            every {REFRESH_MS / 1000}s
            {board && !board.canChangeBranch && ' · showing your branch'}
          </p>
        </div>
        <div className="board__filters">
          {/* Only head office gets this. Someone who works at a shop has it
              decided for them by the server, so no control is offered. */}
          {board?.canChangeBranch && !onlyOneBranch && (
            <label className="field field--inline">
              <span>Branch</span>
              <select
                value={branchFilter ?? ''}
                onChange={(e) => setBranchFilter(e.target.value === '' ? null : e.target.value)}
              >
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="check">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
            <span>Show finished</span>
          </label>
        </div>
      </header>

      {error && <Alert>{error}</Alert>}

      <div className="stats stats--board">
        <div>
          <strong>{board.paidCount}</strong>
          <span className="muted small">to start</span>
        </div>
        <div>
          <strong>{board.preparingCount}</strong>
          <span className="muted small">being made</span>
        </div>
        <div>
          <strong>{board.outForDeliveryCount}</strong>
          <span className="muted small">on the road</span>
        </div>
        <div>
          <strong>{board.readyForCollectionCount}</strong>
          <span className="muted small">ready</span>
        </div>
        <div>
          <strong>{money(board.takingsToday)}</strong>
          <span className="muted small">{board.ordersToday} orders today</span>
        </div>
      </div>

      {board.orders.length === 0 ? (
        <div className="empty">
          <h2>Nothing waiting</h2>
          <p>New paid orders appear here automatically.</p>
        </div>
      ) : (
        <div className="board">
          {board.orders.map((order) => (
            <article key={order.id} className={`ticket ticket--${statusTone(order.status)}`}>
              <header className="ticket__head">
                <div>
                  <strong>{order.orderNumber}</strong>
                  <span className={`pill pill--${statusTone(order.status)}`}>{statusLabel(order.status)}</span>
                </div>
                <span className="ticket__age">
                  {order.minutesSincePaid > 0 ? `${order.minutesSincePaid} min` : 'just now'}
                </span>
              </header>

              <p className="ticket__customer">
                {order.fulfilment === 'Delivery' ? '🛵' : '🏪'} <strong>{order.customerName}</strong>
                {order.customerPhone && (
                  <>
                    {' · '}
                    <a href={`tel:${order.customerPhone}`}>{order.customerPhone}</a>
                  </>
                )}
              </p>

              <ul className="ticket__items">
                {order.items.map((item, index) => (
                  <li key={index}>
                    <strong>{item.quantity} ×</strong> {item.productName}{' '}
                    <span className="muted">({item.variantLabel})</span>
                    {item.addOns.length > 0 && <div className="muted small">+ {item.addOns.join(', ')}</div>}
                    {item.notes && <div className="ticket__note">“{item.notes}”</div>}
                  </li>
                ))}
              </ul>

              {order.delivery && (
                <p className="ticket__address">
                  {order.delivery.address}
                  <span className="muted small"> · {order.delivery.distanceKm} km</span>
                  {order.delivery.instructions && (
                    <span className="ticket__note">“{order.delivery.instructions}”</span>
                  )}
                </p>
              )}

              {order.notes && <p className="ticket__note">Customer note: “{order.notes}”</p>}

              {/* Deliveries only — a collection order has nobody to give it to. */}
              {order.delivery && order.status !== 'Completed' && order.status !== 'Cancelled' && (
                <div className="ticket__driver">
                  <label>
                    <span>Driver</span>
                    <select
                      value={order.assignedDriverId ?? ''}
                      disabled={busyId === order.id}
                      onChange={(e) => assign(order, e.target.value)}
                    >
                      <option value="">Not assigned</option>
                      {/* The driver already on this order is listed even if
                          they have since gone off shift, so reassigning does
                          not silently blank out who is holding it. */}
                      {order.assignedDriverId &&
                        !drivers.some((d) => d.id === order.assignedDriverId) && (
                          <option value={order.assignedDriverId}>
                            {order.assignedDriverName ?? 'Assigned driver'} (off shift)
                          </option>
                        )}
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.fullName} · {d.vehicle}
                        </option>
                      ))}
                    </select>
                  </label>

                  {drivers.length === 0 && !order.assignedDriverId && (
                    <span className="muted small">
                      No drivers on shift. They have to be approved <em>and</em> switched on in
                      their own app before they can be given work.
                    </span>
                  )}
                </div>
              )}

              <footer className="ticket__foot">
                <span>
                  <strong>{money(order.total)}</strong>
                  {order.usedFreeItem && <span className="tag tag--free">FREE ITEM</span>}
                </span>

                <div className="ticket__actions">
                  {order.nextStatuses.map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={status === 'Cancelled' ? 'btn btn--ghost btn--sm' : 'btn btn--primary btn--sm'}
                      disabled={busyId === order.id}
                      onClick={() => advance(order, status)}
                    >
                      {ACTION_LABEL[status] ?? status}
                    </button>
                  ))}
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
